import { memo, useState } from 'react';
import { getSubsystemIcon } from '../../Missions/Satellites/TestCase/svgs/TestCaseSvgs';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import { CheckIconSvg, TriangleSvg, TimeoutIconSvg } from './ReportsSvgs';
import { SUBSYSTEM_HEADER_VISIBLE } from '../constants/reports.constants';
import type { LeftPanelItem } from '../types/reports.types';

// ─── Subsystem chip strip ─────────────────────────────────────────────────────

export const SubsystemChipsInline = memo(function SubsystemChipsInline({
  subsystems,
}: {
  subsystems: string[];
}) {
  const [expanded, setExpanded] = useState(false);

  if (!subsystems.length) return null;

  const visible = subsystems.slice(0, SUBSYSTEM_HEADER_VISIBLE);
  const overflow = subsystems.slice(SUBSYSTEM_HEADER_VISIBLE);
  const hasOverflow = overflow.length > 0;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <div className="rp__sub-chips-inline">
      {visible.map((sub) => (
        <span key={sub} className="rp__sub-chip">{getSubsystemIcon(sub)}{sub}</span>
      ))}
      {expanded && overflow.map((sub) => (
        <span key={sub} className="rp__sub-chip rp__sub-chip--entering">{getSubsystemIcon(sub)}{sub}</span>
      ))}
      {hasOverflow && (
        <span
          role="button"
          tabIndex={0}
          className="rp__sub-chip rp__sub-chip--more"
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle(e as unknown as React.MouseEvent);
            }
          }}
          aria-label={expanded ? 'Show fewer subsystems' : `Show ${overflow.length} more subsystems`}
        >
          {expanded ? '−' : `+${overflow.length}`}
        </span>
      )}
    </div>
  );
});

// ─── Left panel card ──────────────────────────────────────────────────────────

export const LeftPanelCard = memo(function LeftPanelCard({
  item,
  isActive,
  onSelect,
}: {
  item: LeftPanelItem;
  isActive: boolean;
  onSelect: (selectionId: string) => void;
}) {
  const normalizedStatus = String(item.status ?? '').trim().toUpperCase();
  const rawStatusStr = String(item.rawStatus ?? item.status ?? '').trim().toUpperCase();
  const isCancelled = normalizedStatus === 'CANCELLED' || rawStatusStr.includes('ABORT') || rawStatusStr.includes('CANCEL');
  const hasMetrics = item.pass != null || item.fail != null || item.timeout != null || isCancelled;
  const hasTags = item.tags.length > 0;
  const hasDuration = item.duration != null && item.duration !== '';

  const statusClass = normalizedStatus ? `rp__left-card--status-${normalizedStatus.toLowerCase()}` : '';

  return (
    <button
      type="button"
      className={`rp__left-card rp__left-card--${item.type} ${statusClass}${isActive ? ' rp__left-card--active' : ''}`}
      onClick={() => onSelect(item.selectionId)}
      data-testid={`reports-item-${item.selectionId}`}
    >
      {/* Row 1: icon + title | metrics (active) or date (default) */}
      <div className="rp__left-card__row1">
        <div className="rp__left-card__main">
          <span className="rp__left-card__title" data-testid={`reports-item-title-${item.selectionId}`}>
            <CustomToolTip title={item.rawTitle} placement="top">
              <span>
                {item.rawTitle.toUpperCase()}
                {(item as any).runIndex != null && ` (RUN #${(item as any).runIndex})`}
              </span>
            </CustomToolTip>
          </span>
        </div>

        {isActive && hasMetrics && (
          <div className="rp__left-card__metrics">
            {item.pass != null && (
              <span className="rp__left-card__metric rp__left-card__metric--pass">
                <CheckIconSvg color="#CCF54E" />
                <span>{item.pass}</span>
              </span>
            )}
            {item.fail != null && (
              <span className="rp__left-card__metric rp__left-card__metric--fail">
                <TriangleSvg color="#FF6B6B" />
                <span>{item.fail}</span>
              </span>
            )}
            {item.timeout != null && (
              <span className="rp__left-card__metric rp__left-card__metric--timeout">
                <TimeoutIconSvg color="#FFA940" />
                <span>{item.timeout}</span>
              </span>
            )}
            {isCancelled && (
              <span className="rp__left-card__aborted-label" title={item.rawStatus || 'Manual Abort'}>
                {rawStatusStr.includes('ABORT') ? 'Manual Abort' : (item.rawStatus || 'Cancelled')}
              </span>
            )}
          </div>
        )}

        {!isActive && (
          <div className="d-flex align-items-center gap-2">
            {isCancelled && (
              <span className="rp__left-card__aborted-label" title={item.rawStatus || 'Manual Abort'}>
                {rawStatusStr.includes('ABORT') ? 'Manual Abort' : (item.rawStatus || 'Cancelled')}
              </span>
            )}
            {item.meta && <span className="rp__left-card__date">{item.meta}</span>}
          </div>
        )}
      </div>

      {/* Row 2 (active only): tags | duration + date */}
      {isActive && (hasTags || hasDuration || item.meta) && (
        <div className="rp__left-card__row2">
          <div className="rp__left-card__tags-inline">
            {hasTags
              ? item.tags.map((tag, i) => (
                  <span key={tag}>
                    {i > 0 && <span className="rp__left-card__tag-sep"> · </span>}
                    {tag}
                  </span>
                ))
              : null}
          </div>
          <div className="rp__left-card__meta-block">
            {hasDuration && (
              <span className="rp__left-card__duration-line">Duration: {item.duration} sec</span>
            )}
            {item.meta && (
              <span className="rp__left-card__date">{item.meta}</span>
            )}
          </div>
        </div>
      )}
    </button>
  );
});
